import { useState } from 'react';
import {
  Avatar,
  Box,
  Chip,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  SvgIcon,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { useAuth } from '@/shared/auth/useAuth';

const ROLE_COLOR: Record<string, 'primary' | 'secondary' | 'default'> = {
  DJ: 'primary',
  ADMIN: 'secondary',
  GUEST: 'default',
};

type UserMenuProps = {
  tone?: 'light' | 'dark';
};

function BurgerIcon() {
  return (
    <SvgIcon sx={{ fontSize: 18 }}>
      <path d="M3 18h18v-2H3v2zm0-5h18v-2H3v2zm0-7v2h18V6H3z" />
    </SvgIcon>
  );
}

export function UserMenu({ tone = 'light' }: UserMenuProps) {
  const { user, logout } = useAuth();
  const theme = useTheme();
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);

  if (!user) return null;

  const initials = (user.displayName ?? user.email)
    .split(' ')
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  const isDJ = user.role === 'DJ' || user.role === 'ADMIN';
  const isDark = tone === 'dark';

  const close = () => setAnchor(null);

  return (
    <>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Avatar
          src={user.avatarUrl ?? undefined}
          sx={{
            width: 34,
            height: 34,
            bgcolor: 'primary.main',
            fontSize: 13,
            fontWeight: 700,
            boxShadow: isDark
              ? `0 0 0 1px ${alpha(theme.palette.common.white, 0.2)}, 0 10px 24px ${alpha(theme.palette.secondary.main, 0.24)}`
              : 'none',
          }}
        >
          {initials}
        </Avatar>
        <IconButton
          onClick={(e) => setAnchor(e.currentTarget)}
          size="small"
          aria-label="Open menu"
          sx={{
            border: '1px solid',
            borderColor: isDark ? alpha(theme.palette.common.white, 0.22) : 'divider',
            borderRadius: 1.5,
            p: 0.75,
            color: isDark ? alpha(theme.palette.common.white, 0.78) : 'text.secondary',
            bgcolor: isDark ? alpha(theme.palette.common.white, 0.06) : 'transparent',
            '&:hover': {
              bgcolor: isDark ? alpha(theme.palette.secondary.main, 0.18) : 'grey.100',
              borderColor: isDark ? alpha(theme.palette.secondary.light, 0.45) : 'divider',
              color: isDark ? 'common.white' : 'text.primary',
            },
          }}
        >
          <BurgerIcon />
        </IconButton>
      </Stack>

      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={close}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        slotProps={{ paper: { sx: { mt: 1, minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle2" noWrap fontWeight={700}>
            {user.displayName ?? user.email}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {user.email}
          </Typography>
          {isDJ && (
            <Box sx={{ mt: 1 }}>
              <Chip label={user.role} size="small" color={ROLE_COLOR[user.role] ?? 'default'} />
            </Box>
          )}
        </Box>

        <Divider />

        <MenuItem
          onClick={() => { close(); logout(); }}
          sx={{ color: 'error.main', mt: isDJ ? 0 : 0.5, mb: 0.5 }}
        >
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
